import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/context/CartContext";
import { resolveImage, formatPrice } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, shipping, total } = useCart();
  const navigate = useNavigate();

  if (items.length === 0)
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="empty-cart">
        <ShoppingBag className="mx-auto h-14 w-14 text-slate-300" />
        <h1 className="mt-6 font-heading text-2xl font-bold text-slate-900">Coșul tău este gol</h1>
        <p className="mt-2 text-slate-500">Descoperă produsele noastre și adaugă-le în coș.</p>
        <Button asChild className="mt-8 rounded-full"><Link to="/magazin">Mergi la magazin</Link></Button>
      </div>
    );

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" data-testid="cart-page">
      <h1 className="mb-8 font-heading text-3xl font-bold tracking-tight text-slate-900">Coșul meu</h1>
      <div className="grid gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="divide-y divide-slate-200 rounded-xl border border-slate-200">
            {items.map((item) => (
              <div key={item.product_id} className="flex flex-wrap items-center gap-4 p-4" data-testid={`cart-item-${item.product_id}`}>
                <img src={resolveImage(item.image)} alt={item.name} className="h-16 w-16 shrink-0 rounded-lg border border-slate-100 object-contain p-1 sm:h-20 sm:w-20" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-sm font-semibold text-slate-900 line-clamp-2">{item.name}</h3>
                  <p className="mt-1 text-sm text-slate-500">{formatPrice(item.price)}</p>
                </div>
                <div className="flex w-full items-center justify-between gap-3 sm:ml-auto sm:w-auto sm:justify-end sm:gap-4">
                  <div className="flex shrink-0 items-center rounded-full border border-slate-300">
                    <button onClick={() => updateQty(item.product_id, item.qty - 1)} className="p-2 text-slate-600 hover:text-primary" aria-label="Scade cantitatea"><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-7 text-center text-sm font-medium">{item.qty}</span>
                    <button onClick={() => updateQty(item.product_id, item.qty + 1)} className="p-2 text-slate-600 hover:text-primary" aria-label="Crește cantitatea"><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="w-20 shrink-0 whitespace-nowrap text-right font-heading text-sm font-semibold text-slate-900 sm:w-24 sm:text-base">{formatPrice(item.price * item.qty)}</div>
                  <button onClick={() => removeItem(item.product_id)} className="shrink-0 p-2 text-slate-400 hover:text-destructive" data-testid={`remove-${item.product_id}`} aria-label="Elimină din coș">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6">
            <h2 className="font-heading text-lg font-bold text-slate-900">Sumar comandă</h2>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-medium">{formatPrice(subtotal)}</span></div>
              <div className="flex justify-between">
                <span className="text-slate-600">Transport</span>
                <span className="font-semibold text-success" data-testid="cart-shipping">GRATUIT</span>
              </div>
              <Separator />
              <div className="flex justify-between text-base"><span className="font-semibold">Total</span><span className="font-heading font-bold text-primary">{formatPrice(total)}</span></div>
            </div>
            <Button onClick={() => navigate("/finalizare")} className="mt-6 w-full rounded-full hover:bg-primary/90" size="lg" data-testid="checkout-button">
              Finalizează comanda <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Link to="/magazin" className="mt-4 block text-center text-sm text-slate-500 hover:text-primary">Continuă cumpărăturile</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
